import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import QuestionCard from '../components/QuestionCard';
import useQuestionStore from '../stores/questionStore';
import useProgressStore from '../stores/progressStore';
import { getEra, isLiteratureQuestion, ERA_OPTIONS } from '../utils/questionMeta';

// ── Confidence dot color ─────────────────────────────────────────────────
function ConfidenceDot({ rating }) {
  if (!rating) return <span className="w-2 h-2 rounded-full bg-slate-600 shrink-0" title="Unrated" />;
  const colors = {
    know: 'bg-emerald-400',
    kinda: 'bg-amber-400',
    unsure: 'bg-red-400',
  };
  const labels = { know: 'Know it', kinda: 'Kinda', unsure: "Don't Know" };
  return <span className={`w-2 h-2 rounded-full ${colors[rating]} shrink-0`} title={labels[rating]} />;
}

export default function QuestionBank() {
  const questions = useQuestionStore((s) => s.questions);
  const userAnswers = useQuestionStore((s) => s.userAnswers);
  const confidenceRatings = useQuestionStore((s) => s.confidenceRatings);
  const flaggedQuestions = useQuestionStore((s) => s.flaggedQuestions);
  const toggleFlag = useQuestionStore((s) => s.toggleFlag);
  const answerQuestion = useQuestionStore((s) => s.answerQuestion);
  const recordAnswer = useProgressStore((s) => s.recordAnswer);
  const categories = useQuestionStore((s) => s.getCategories());
  const [search, setSearch] = useState('');
  const [selectedCats, setSelectedCats] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [eraFilter, setEraFilter] = useState('');
  const [literatureOnly, setLiteratureOnly] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(0);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const perPage = 25;

  const filtered = useMemo(() => {
    return questions.filter(q => {
      if (selectedCats.length && !selectedCats.includes(q.category)) return false;
      if (statusFilter === 'answered' && !userAnswers[q.id]) return false;
      if (statusFilter === 'unanswered' && userAnswers[q.id]) return false;
      if (statusFilter === 'flagged' && !flaggedQuestions.has(q.id)) return false;
      if (statusFilter === 'correct' && !userAnswers[q.id]?.isCorrect) return false;
      if (statusFilter === 'incorrect' && (!userAnswers[q.id] || userAnswers[q.id]?.isCorrect)) return false;
      if (statusFilter === 'unsure') {
        const c = confidenceRatings[q.id];
        if (c !== 'unsure' && c !== 'kinda') return false;
      }
      if (eraFilter && getEra(q) !== eraFilter) return false;
      if (literatureOnly && !isLiteratureQuestion(q)) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!q.question.toLowerCase().includes(s) && !q.category.toLowerCase().includes(s) && !q.id.toString().toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [questions, selectedCats, statusFilter, eraFilter, literatureOnly, search, userAnswers, flaggedQuestions, confidenceRatings]);

  const paginated = filtered.slice(page * perPage, (page + 1) * perPage);
  const totalPages = Math.ceil(filtered.length / perPage);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Question Bank</h1>
          <p className="text-slate-400 text-sm">{filtered.length} of {questions.length} questions</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-3 space-y-2">
        <input
          type="text"
          placeholder="Search questions..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-400 focus:outline-none focus:border-teal-500"
        />

        {/* Status pills — always visible */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5 flex-1">
            {[
              { value: '', label: 'All' },
              { value: 'unanswered', label: 'Unseen' },
              { value: 'incorrect', label: 'Missed' },
              { value: 'unsure', label: "Don't Know" },
              { value: 'flagged', label: '🚩 Flagged' },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => { setStatusFilter(opt.value); setPage(0); }}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  statusFilter === opt.value
                    ? 'bg-teal-500/20 text-teal-400 border border-teal-500/40'
                    : 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setFiltersExpanded(v => !v)}
            className="text-xs text-slate-500 hover:text-slate-300 shrink-0 px-2 py-1"
          >
            {filtersExpanded ? '▲ Less' : '▼ More'}
          </button>
        </div>

        {/* Expanded filters */}
        <AnimatePresence>
          {filtersExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden space-y-2"
            >
              {/* Era + Literature */}
              <div className="flex flex-wrap gap-1.5">
                {ERA_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setEraFilter(opt.value); setPage(0); }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      eraFilter === opt.value
                        ? 'bg-teal-500/20 text-teal-400 border border-teal-500/40'
                        : 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    {opt.emoji ? `${opt.emoji} ` : ''}{opt.label}
                  </button>
                ))}
                <button
                  onClick={() => { setLiteratureOnly(v => !v); setPage(0); }}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    literatureOnly
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                      : 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
                  }`}
                >
                  📚 Literature
                </button>
              </div>

              {/* Categories */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-slate-500 font-medium">Categories</span>
                  <div className="flex gap-2">
                    <button onClick={() => { setSelectedCats([...categories]); setPage(0); }} className="text-xs text-teal-400 hover:text-teal-300">All</button>
                    <span className="text-slate-600">·</span>
                    <button onClick={() => { setSelectedCats([]); setPage(0); }} className="text-xs text-slate-500 hover:text-slate-300">None</button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => { setSelectedCats(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]); setPage(0); }}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        selectedCats.includes(cat)
                          ? 'bg-teal-500/20 text-teal-400 border-teal-500/40'
                          : 'bg-slate-700 text-slate-400 border-slate-600 hover:border-slate-500'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Question list */}
      <div className="space-y-2">
        {paginated.map((q, i) => (
          <motion.div
            key={q.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.015 }}
          >
            {expandedId === q.id ? (
              <div>
                <QuestionCard
                  question={q}
                  showAnswer={true}
                  onAnswer={(idx) => {
                    const isCorrect = answerQuestion(q.id, idx);
                    recordAnswer(isCorrect);
                  }}
                  onFlag={() => toggleFlag(q.id)}
                  isFlagged={flaggedQuestions.has(q.id)}
                  userAnswer={userAnswers[q.id]}
                  selectedAnswer={userAnswers[q.id]?.selectedIndex ?? null}
                  readOnly={!!userAnswers[q.id]}
                />
                <button onClick={() => setExpandedId(null)} className="text-xs text-slate-500 mt-2 hover:text-slate-300">
                  ▲ Collapse
                </button>
              </div>
            ) : (
              <button
                onClick={() => setExpandedId(q.id)}
                className="w-full text-left card p-3.5 hover:border-teal-500/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ConfidenceDot rating={confidenceRatings[q.id]} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{q.question}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-teal-400 bg-teal-500/10 px-1.5 py-0.5 rounded">{q.category}</span>
                      {userAnswers[q.id] && (
                        <span className={`text-xs font-semibold ${userAnswers[q.id].isCorrect ? 'text-emerald-400' : 'text-red-400'}`}>
                          {userAnswers[q.id].isCorrect ? '✓' : '✗'}
                        </span>
                      )}
                      {flaggedQuestions.has(q.id) && <span className="text-xs">🚩</span>}
                    </div>
                  </div>
                </div>
              </button>
            )}
          </motion.div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="btn-secondary text-xs px-3 py-1 disabled:opacity-30">
            ← Prev
          </button>
          <span className="text-sm text-slate-400">Page {page + 1} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="btn-secondary text-xs px-3 py-1 disabled:opacity-30">
            Next →
          </button>
        </div>
      )}

      {filtered.length === 0 && (
        <div className="card p-8 text-center">
          <p className="text-slate-500">No questions match your filters</p>
        </div>
      )}
    </div>
  );
}
